import { PropertyType } from "@/app/[locale]/(public)/rent/[locationSlug]/_components/property-type/utils";
import { locationInfo } from "./info";
import { rentPropertyTypes } from "./property-type";
import { rentLocations } from "./rent-location";
import { resolveLocalizedLocationName } from "./name";

export type LocationOption = {
  label: string;
  value: string;
};

const normalizeLocationName = (value: string) =>
  value.trim().toLocaleLowerCase();

const rentLocationByName = new Map(
  Object.values(rentLocations).flatMap((location) =>
    location.names.map(
      (name) => [normalizeLocationName(name), location] as const,
    ),
  ),
);

export const getRentLocationDisplayName = (
  location: (typeof rentLocations)[string],
  locale: string,
) =>
  resolveLocalizedLocationName(
    {
      name: location.names[0],
      localizedNames: location.localizedNames,
    },
    locale,
  );

export const getLocationDisplayName = (name: string, locale: string) => {
  const location = rentLocationByName.get(normalizeLocationName(name));

  return location ? getRentLocationDisplayName(location, locale) : name;
};

/**
 * Slug lookup by any name a location answers to. Every entry in the generated
 * file has a `/rent/:slug` page regardless of type, so provinces, boroughs and
 * neighbourhoods resolve here just as cities do.
 *
 * Names are not unique — plenty of cities have a "Centrum" — so a name shared
 * by more than one location resolves to null rather than silently linking to
 * whichever slug happened to be first.
 */
const rentLocationSlugByName = (() => {
  const map = new Map<string, string | null>();

  for (const [slug, location] of Object.entries(rentLocations)) {
    for (const name of location.names) {
      const key = normalizeLocationName(name);
      if (!map.has(key)) {
        map.set(key, slug);
        continue;
      }
      if (map.get(key) !== slug) map.set(key, null);
    }
  }

  return map;
})();

export const getRentLocationSlugByName = (name: string) =>
  rentLocationSlugByName.get(normalizeLocationName(name)) ?? null;

/**
 * `/rent/:slug` for a location name, or null when the name is unknown,
 * ambiguous, or does not sit at the expected geographic level.
 */
export const getRentLocationHrefByName = (
  name: string,
  expectedType?: (typeof rentLocations)[string]["type"],
) => {
  const slug = getRentLocationSlugByName(name);
  if (!slug) return null;
  if (expectedType && rentLocations[slug]?.type !== expectedType) return null;
  return `/rent/${slug}`;
};

const getLocationOptions = (
  names: string[],
  locale: string,
): LocationOption[] =>
  names.map((name) => ({
    value: name,
    label: getLocationDisplayName(name, locale),
  }));

export const getTopCitiesSlugNames = (limit: number = 10, locale = "en") => {
  const topCitySlugs = Object.keys(rentLocations)
    .filter((slug) => rentLocations[slug].type === "CITY")
    .sort((a, b) => {
      return (rentLocations[b].count || 0) - (rentLocations[a].count || 0);
    })
    .slice(0, limit);

  const topCities = topCitySlugs.map((slug) => {
    return {
      slug,
      canonicalName: rentLocations[slug].names[0],
      name: getRentLocationDisplayName(rentLocations[slug], locale),
    };
  });

  return topCities;
};

export const topCities = getTopCitiesSlugNames(12).map(
  (city) => city.canonicalName,
);

const sortCitiesWithTopCities = (cities: string[]) => {
  const topCitiesSet = new Set(topCities);
  const sortedCities = cities.sort((a, b) => {
    const isATopCity = topCitiesSet.has(a);
    const isBTopCity = topCitiesSet.has(b);

    if (isATopCity && !isBTopCity) return -1;
    if (!isATopCity && isBTopCity) return 1;

    return a.localeCompare(b);
  });
  return sortedCities;
};

export const getAllLocationCities = () => {
  const allCities: string[] = [];
  Object.values(locationInfo).forEach((cities) => {
    allCities.push(...cities);
  });
  const result = Array.from(new Set(allCities)).sort();

  return sortCitiesWithTopCities(result);
};

export const getAllLocationCityOptions = (locale: string) =>
  getLocationOptions(getAllLocationCities(), locale);

export const getAllProvinces = () => {
  return Object.keys(locationInfo).sort();
};

export const getAllProvinceOptions = (locale: string) =>
  getLocationOptions(getAllProvinces(), locale);

export const getCitiesByProvince = (province: string) => {
  const result = locationInfo[province] || [];

  return sortCitiesWithTopCities(result);
};

export const getCityOptionsByProvince = (province: string, locale: string) =>
  getLocationOptions(getCitiesByProvince(province), locale);

export const getRentLocation = (locationSlug: string) => {
  if (!rentLocations[locationSlug]) {
    return null;
  }

  return rentLocations[locationSlug];
};

export const doesLocationHavePropertyType = (
  locationSlug: string,
  propertyType: string,
) => {
  if (
    rentPropertyTypes[locationSlug] &&
    propertyType in rentPropertyTypes[locationSlug]
  ) {
    return true;
  }
  return false;
};

export const getLocationPropertyTypeData = (locationSlug: string) => {
  if (!rentPropertyTypes[locationSlug]) {
    return null;
  }

  return rentPropertyTypes[locationSlug];
};
export const getAllPropertyTypes = (locationSlug: string, skip?: string[]) => {
  if (!rentPropertyTypes[locationSlug]) {
    return [];
  }

  const items = (
    Object.keys(rentPropertyTypes[locationSlug]) as Array<PropertyType>
  )
    .filter((item) => {
      if (skip && skip.includes(item)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.localeCompare(b));

  return items;
};
